import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, X, Sparkles, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import ProposalEditor from '../components/ProposalEditor';

export default function WhatsAppInbox() {
  const { API } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [manualText, setManualText] = useState('');
  const [editingProposal, setEditingProposal] = useState(null);
  const [activeDocId, setActiveDocId] = useState(null);

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/documents`);
        // Ensure data is array
        setDocuments(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error loading documents:", error);
        toast.error('Error al cargar documentos');
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };
    loadDocuments();
  }, [API]);

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/documents`);
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error al cargar documentos');
    }
  };

  const processWithAI = async (fileData, textInput, docId = null, mimeType = null) => {
    const toastId = toast.loading('Analizando con IA...');
    try {
      const payload = {};
      if (fileData) {
        payload.file_data = fileData; // Base64
        payload.mime_type = mimeType || 'image/jpeg'; // Send mime type
      }
      if (textInput) {
        payload.text_input = textInput;
      }

      const response = await axios.post(`${API}/ai/process-document`, payload);
      const proposal = response.data;

      // Persist proposal if a document exists
      if (docId) {
        await axios.put(`${API}/whatsapp/documents/${docId}/proposal`, {
          formatted_proposal: proposal
        });
      }

      toast.dismiss(toastId);
      toast.success('Propuesta generada');

      setEditingProposal(proposal);
      setActiveDocId(docId);

    } catch (error) {
      toast.dismiss(toastId);
      console.error(error);
      // Show actual error message from backend
      const errorMessage = error.response?.data?.detail || 'Error al procesar con IA';
      toast.error(errorMessage);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualText.trim()) return;
    await processWithAI(null, manualText);
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // 1. Upload file to save it
    const formData = new FormData();
    formData.append('file', file);
    formData.append('phone', 'manual_upload');

    let docId = null;
    try {
      const uploadResp = await axios.post(`${API}/whatsapp/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      docId = uploadResp.data.id;
      loadDocuments();
    } catch (error) {
      toast.error('Error al subir documento');
      return;
    }

    // 2. Read file as base64 for AI
    const reader = new FileReader();
    reader.onload = async () => {
      const base64String = reader.result;
      setProcessingId(docId);
      await processWithAI(base64String, null, docId, file.type); // Pass mime type
      setProcessingId(null);
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const handleSaveProposal = async (transactionData) => {
    try {
      // 1. Create transaction
      const txResp = await axios.post(`${API}/transactions`, transactionData);

      // 2. If linked to a document, update document status
      if (activeDocId) {
        await axios.put(`${API}/whatsapp/documents/${activeDocId}/status?status=processed&transaction_id=${txResp.data.id}`);
      }

      toast.success('Registro guardado. Pendiente de cruce con SII.');
      setEditingProposal(null);
      setActiveDocId(null);
      setManualText('');
      loadDocuments();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar registro');
    }
  };

  const handleCancelProposal = () => {
    setEditingProposal(null);
    setActiveDocId(null);
  };

  const updateStatus = async (docId, status) => {
    try {
      await axios.put(`${API}/whatsapp/documents/${docId}/status?status=${status}`);
      toast.success('Estado actualizado');
      loadDocuments();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Re-process an existing document
  const handleReprocess = async (doc) => {
    // If we already have a proposal saved, use it!
    if (doc.ai_proposal) {
      setEditingProposal(doc.ai_proposal);
      setActiveDocId(doc.id);
      toast.info("Cargando propuesta guardada");
      return;
    }

    if (doc.file_data) {
      setProcessingId(doc.id);
      await processWithAI(doc.file_data, null, doc.id);
      setProcessingId(null);
    } else if (doc.text_content) {
      setProcessingId(doc.id);
      await processWithAI(null, doc.text_content, doc.id);
      setProcessingId(null);
    } else {
      toast.error("No se puede reprocesar: falta contenido");
    }
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  // Safety check for documents array
  const safeDocuments = Array.isArray(documents) ? documents : [];

  return (
    <div className="p-8 space-y-6" data-testid="inbox-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          Bandeja de Entrada
        </h1>
        <p className="text-muted-foreground">Procesa documentos y descripciones para generar registros contables con IA.</p>
      </div>

      {editingProposal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-4xl animate-in fade-in zoom-in duration-300">
            <ProposalEditor
              proposal={editingProposal}
              onSave={handleSaveProposal}
              onCancel={handleCancelProposal}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Manual Input */}
        <Card>
          <CardHeader>
            <CardTitle>Ingreso Rápido</CardTitle>
            <CardDescription>Describe el gasto o ingreso para que la IA lo procese.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Ej: Almuerzo con cliente Juan Pérez en Restaurante X, pagado con tarjeta empresa. Valor $45.000"
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              className="min-h-[120px]"
            />
            <Button onClick={handleManualSubmit} disabled={!manualText.trim()}>
              <Wand2 className="mr-2 h-4 w-4" />
              Procesar Texto
            </Button>
          </CardContent>
        </Card>

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Subir Documento</CardTitle>
            <CardDescription>Facturas, boletas o comprobantes.</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              data-testid="dropzone"
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all h-[180px] flex flex-col items-center justify-center ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-10 w-10 text-muted-foreground mb-4" />
              {isDragActive ? (
                <p>Suelta el archivo aquí...</p>
              ) : (
                <>
                  <p className="font-medium">Arrastra o haz clic</p>
                  <p className="text-sm text-muted-foreground mt-1">Imágenes o PDF</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Documentos Pendientes</CardTitle>
          <CardDescription>Archivos subidos que requieren procesamiento.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {safeDocuments.filter(d => d.status === 'pending').length === 0 && (
              <p className="text-center text-muted-foreground py-8">No hay documentos pendientes.</p>
            )}

            {safeDocuments.filter(d => d.status === 'pending').map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium truncate max-w-[300px]">
                      {doc.file_url || `📝 "${doc.text_content?.substring(0, 30)}..."`}
                    </p>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span>{new Date(doc.date).toLocaleDateString('es-CL')}</span>
                      <span>•</span>
                      <span>{doc.phone === 'manual_upload' ? 'Subida Manual' : 'WhatsApp'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleReprocess(doc)}
                    disabled={processingId === doc.id}
                  >
                    {processingId === doc.id ? (
                      <span className="animate-spin mr-2">⏳</span>
                    ) : (
                      <Wand2 className="mr-2 h-4 w-4" />
                    )}
                    Procesar
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateStatus(doc.id, 'rejected')}
                  >
                    <X className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Processed History (Collapsed or simpler view) */}
      <div className="pt-8">
        <h3 className="text-lg font-semibold mb-4 text-muted-foreground">Historial Reciente</h3>
        <div className="space-y-2 opacity-60 hover:opacity-100 transition-opacity">
          {safeDocuments.filter(d => d.status !== 'pending').slice(0, 5).map((doc) => (
            <div key={doc.id} className="flex justify-between p-3 bg-muted rounded text-sm">
              <span>{doc.file_url}</span>
              <span className={doc.status === 'processed' ? 'text-green-600' : 'text-red-600'}>
                {doc.status === 'processed' ? 'Procesado' : 'Rechazado'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}